import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Radio,
  Stack,
} from "@mui/material";
import DialogHeader from "../DialogHeader";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { selectActiveSession, selectSelectedSchool } from "app/modules/schools/selectors";

const UniversalExportDialog = ({
  open = false,
  onClose = () => {},
  
  // Direct props - headers and data
  pdfHeaders = [], // Array of column headers for PDF
  excelHeaders = [], // Array of column headers for Excel
  pdfData = [], // Array of data objects for PDF
  excelData = [], // Array of data objects for Excel
  
  fileName = "Export", // Base filename without extension
  
  // Export type
  exportType = "excel", // "excel" or "pdf" - determines which export to perform
  
  // Feature toggles
  showSelectAll = true,
  showOrientation = false,
  
  // PDF specific
  pdfTitle = "",
  pdfSubtitle = "",
  showSchoolHeader = true,
  
  // Customization
  onBeforeExport = null, // Callback before export (data, selectedFields) => transformedData
  extraHeaderData = [], // Additional header rows for excel/pdf
  summaryTableData = [], // Array of { label, value } rows shown above export table
}) => {
  // Use appropriate headers and data based on exportType
  const headers = exportType === "excel" ? excelHeaders : pdfHeaders;
  const tableData = exportType === "excel" ? excelData : pdfData;
  
  const [selectedFields, setSelectedFields] = useState(headers);
  const [orientation, setOrientation] = useState("landscape");
  
  const selectedSchool = useSelector(selectSelectedSchool);
  const session = useSelector(selectActiveSession);

  // Update selected fields when headers change (when exportType changes)
  React.useEffect(() => {
    setSelectedFields(headers);
  }, [exportType, headers]);

  const handleCheckboxChange = (option, isChecked) => {
    if (isChecked) {
      setSelectedFields((prev) => [...prev, option]);
    } else {
      setSelectedFields((prev) => prev.filter((field) => field !== option));
    }
  };

  const handleSelectAllChange = (isChecked) => {
    setSelectedFields(isChecked ? headers : []);
  };

  const handleExportExcel = () => {
    // Transform data based on selected fields
    let exportData = tableData.map((item) => {
      const filteredItem = {};
      selectedFields.forEach((field) => {
        filteredItem[field] = item[field] ?? "";
      });
      return filteredItem;
    });

    // Allow parent to transform data if needed
    if (onBeforeExport) {
      exportData = onBeforeExport(exportData, selectedFields, "excel");
    }

    const summaryHeaders = Array.isArray(summaryTableData)
      ? summaryTableData
          .filter((item) => item?.label)
          .map((item) => item.label)
      : [];
    const summaryValues = Array.isArray(summaryTableData)
      ? summaryTableData
          .filter((item) => item?.label)
          .map((item) => item?.value ?? "")
      : [];

    // Create worksheet data
    const worksheetData = [
      [], // Empty row
      [], // Empty row
      [], // Empty row for header info
      ...(summaryHeaders.length > 0 ? [summaryHeaders, summaryValues, []] : []),
      selectedFields, // Headers
      ...exportData.map((item) => Object.values(item)), // Data rows
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Export");

    // Add header information
    const addDataToSheet = (worksheet, data, origin) => {
      XLSX.utils.sheet_add_aoa(worksheet, [[data]], { origin });
    };

    if (showSchoolHeader) {
      const dataPoints = [
        { data: `School Name: ${selectedSchool?.schoolName || ""}`, origin: "A1" },
        { data: `Session: ${session || ""}`, origin: "A2" },
        { data: `Downloaded On: ${dayjs().format("DD-MM-YYYY")}`, origin: "E2" },
        ...extraHeaderData.map((item, index) => ({
          data: item.value,
          origin: item.origin || `A${index + 4}`,
        })),
      ];
      dataPoints.forEach(({ data, origin }) => addDataToSheet(worksheet, data, origin));
    }

    XLSX.writeFile(workbook, `${fileName}.xlsx`);
    onClose();
  };

  const handleExportPdf = () => {
    const doc = new jsPDF({
      orientation: orientation,
    });

    // Transform data based on selected fields
    let exportData = tableData.map((item) => {
      const filteredItem = {};
      selectedFields.forEach((field) => {
        filteredItem[field] = item[field] ?? "";
      });
      return filteredItem;
    });

    // Allow parent to transform data if needed
    if (onBeforeExport) {
      exportData = onBeforeExport(exportData, selectedFields, "pdf");
    }

    // Create table rows
    const rows = exportData.map((item) => selectedFields.map((field) => item[field]));

    const tableStyles = {
      headStyles: {
        fillColor: [182, 200, 214],
        textColor: 0,
        fontSize: 8,
        cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
      },
      bodyStyles: {
        fontSize: 7,
        cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
        textColor: [0, 0, 0],
      },
      theme: "grid",
    };

    let startY = showSchoolHeader ? 40 : 20;

    // Add school header if enabled
    if (showSchoolHeader) {
      doc.setFontSize(16);
      doc.text(selectedSchool?.schoolName || "School Name", 14, 15);
      doc.setFontSize(10);
      doc.text(`Session: ${session || ""}`, 14, 22);
      doc.text(`Downloaded: ${dayjs().format("DD-MM-YYYY")}`, 14, 28);
      
      if (pdfTitle) {
        doc.setFontSize(12);
        doc.text(pdfTitle, 14, 35);
      }
    }

    if (extraHeaderData?.length > 0) {
      const maxWidth = orientation === "landscape" ? 270 : 180;
      doc.setFontSize(10);

      extraHeaderData.forEach((item) => {
        const text = item?.text || item?.value || item?.data || "";
        if (!text) return;
        const lines = doc.splitTextToSize(String(text), maxWidth);
        doc.text(lines, 14, startY);
        startY += lines.length * 5;
      });

      startY += 3;
    }

    if (Array.isArray(summaryTableData) && summaryTableData.length > 0) {
      const summaryHeaders = summaryTableData
        .filter((item) => item?.label)
        .map((item) => item.label);
      const summaryValues = summaryTableData
        .filter((item) => item?.label)
        .map((item) => item?.value ?? "");

      if (summaryHeaders.length > 0) {
        autoTable(doc, {
          head: [summaryHeaders],
          body: [summaryValues],
          startY,
          theme: "grid",
          styles: { fontSize: 8 },
          headStyles: {
            fillColor: [230, 236, 244],
            textColor: 20,
            fontSize: 8,
          },
        });

        startY = (doc.lastAutoTable?.finalY || startY) + 4;
      }
    }

    autoTable(doc, {
      head: [selectedFields],
      body: rows,
      startY,
      ...tableStyles,
    });

    doc.save(`${fileName}.pdf`);
    onClose();
  };

  const handleExport = () => {
    if (exportType === "excel") {
      handleExportExcel();
    } else {
      handleExportPdf();
    }
  };

  const dialogTitle = exportType === "excel" ? "Excel Export Options" : "PDF Export Options";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { minWidth: "500px" },
      }}
    >
      <DialogHeader title={dialogTitle} handleClose={onClose} />
      
      <DialogContent>
        {/* Select All & Orientation */}
        {(showSelectAll || (showOrientation && exportType === "pdf")) && (
          <Box
            sx={{
              mb: 2,
              pb: 2,
              borderBottom: "1px solid #e0e0e0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {showSelectAll && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedFields.length === headers.length}
                    onChange={(e) => handleSelectAllChange(e.target.checked)}
                  />
                }
                label="Select All"
              />
            )}

            {showOrientation && exportType === "pdf" && (
              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={
                    <Radio
                      checked={orientation === "landscape"}
                      onChange={() => setOrientation("landscape")}
                    />
                  }
                  label="Landscape"
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={orientation === "portrait"}
                      onChange={() => setOrientation("portrait")}
                    />
                  }
                  label="Portrait"
                />
              </Stack>
            )}
          </Box>
        )}

        {/* Field Selection */}
        <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
          {headers.map((option) => (
            <Stack direction="row" key={option}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedFields.includes(option)}
                    onChange={(e) => handleCheckboxChange(option, e.target.checked)}
                  />
                }
                label={option}
              />
            </Stack>
          ))}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleExport}
          disabled={selectedFields.length === 0}
        >
          Export {exportType === "excel" ? "Excel" : "PDF"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UniversalExportDialog;